import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLazyQuery } from '@apollo/client';

import { QUERY_POSTHOG_FEATURE_FLAG } from '@/apollo/queries';
import { logError } from '@/utils/logError';

export enum ActiveFeatureFlag {
  EMPLOYER_NAME_EXPERIMENT = '2025_07-global-use_employer_name_poc-experiment', // owner: @smurmus-bighealth
  SPARK_AUTH = '2026_03-spark_auth', // owner: @dguarin-bighealth
}

export const usePostHogFeatureFlags = () => {
  const [fetchFlagGql] = useLazyQuery(QUERY_POSTHOG_FEATURE_FLAG);

  const getFlag = useCallback(
    async ({
      email,
      featureFlagName,
    }: {
      email: string;
      featureFlagName: ActiveFeatureFlag;
    }) => {
      const { data } = await fetchFlagGql({
        variables: { email, featureFlagName },
      });

      return data?.featureFlag ?? null;
    },
    [fetchFlagGql],
  );

  return { getFlag };
};

export type FeatureFlagValue = string | boolean | string[];

/** Map of already-resolved variants. */
type VariantsState = Partial<Record<ActiveFeatureFlag, FeatureFlagValue>>;

interface FeatureFlagCtxValue {
  getVariant: (flag: ActiveFeatureFlag, fallback?: FeatureFlagValue) => FeatureFlagValue | undefined;
  /** Ensure the flag is fetched (no-op if already cached/in-flight). */
  ensureFlag: (flag: ActiveFeatureFlag) => void;
}

const FeatureFlagContext = createContext<FeatureFlagCtxValue | null>(null);

export const FeatureFlagProvider: React.FC<{
  /** User email (or distinctId) to query PostHog. */
  email: string | null | undefined;
  /** Optional default variants to use before real flag resolves. */
  fallbackMap?: VariantsState;
}> = ({ email, fallbackMap = {}, children }) => {
  const { getFlag } = usePostHogFeatureFlags();
  // Only store values we have fetched from the server.
  const [variants, setVariants] = useState<VariantsState>({});
  // track in-flight requests
  const loadingRef = useRef<Record<ActiveFeatureFlag, boolean>>({} as Record<ActiveFeatureFlag, boolean>);

  const fetchAndStore = useCallback(
    async (flag: ActiveFeatureFlag) => {
      if (loadingRef.current[flag] || variants[flag] !== undefined || !email) {
        return;
      }
      loadingRef.current[flag] = true;
      try {
        const featureFlag = await getFlag({ email, featureFlagName: flag });
        const value = featureFlag?.value?.[flag] as FeatureFlagValue | undefined;
        if (value !== undefined) {
          setVariants((prev) => ({ ...prev, [flag]: value }));
        }
      } catch (e) {
        // don't throw a visible error if the flag fetch fails
        logError('[usePostHogFeatureFlags]', 'feature flag fetch failed', e, { level: 'warning' });
      } finally {
        loadingRef.current[flag] = false;
      }
    },
    [email, getFlag],
  );

  const ctxValue = useMemo<FeatureFlagCtxValue>(
    () => ({
      getVariant: (flag, fallback?: FeatureFlagValue) => (
        variants[flag] ?? (fallbackMap[flag] ?? fallback)
      ),
      ensureFlag: (flag: ActiveFeatureFlag) => {
        if (variants[flag] !== undefined || loadingRef.current[flag]) {
          return;
        }
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        fetchAndStore(flag);
      },
    }),
    [variants, fetchAndStore, fallbackMap],
  );

  /* If the email changes, flush cached variants for new user */
  useEffect(() => {
    setVariants({});
    loadingRef.current = {} as Record<ActiveFeatureFlag, boolean>;
  }, [email]);

  return <FeatureFlagContext.Provider value={ctxValue}>{children}</FeatureFlagContext.Provider>;
};

export const useFeatureFlag = (
  flag: ActiveFeatureFlag,
  fallback?: FeatureFlagValue,
): FeatureFlagValue | undefined => {
  const ctx = useContext(FeatureFlagContext);
  if (ctx === null) {
    throw new Error('useFeatureFlag must be used within a <FeatureFlagProvider>.');
  }

  const { getVariant, ensureFlag } = ctx;

  useEffect(() => {
    ensureFlag(flag);
  }, [flag, ensureFlag]);

  return getVariant(flag, fallback);
};
